import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Button, Chip, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress, Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useAuth } from '../contexts/AuthContext';
import { getMyFeatureRequests } from '../services/api';

const STATUS_COLORS = {
  submitted: 'default',
  under_review: 'info',
  backlog: 'secondary',
  in_progress: 'warning',
  completed: 'success',
  declined: 'error',
};

const STATUS_LABELS = {
  submitted: 'Submitted',
  under_review: 'Under review',
  backlog: 'On the roadmap',
  in_progress: 'In progress',
  completed: 'Completed',
  declined: 'Declined',
};

function formatDate(s) {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (e) {
    return s;
  }
}

export default function MyFeatureRequestsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await getMyFeatureRequests();
      setRequests(data);
    } catch (e) {
      setError('Could not load your feature requests.');
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      load();
    }
  }, [isAuthenticated, authLoading, load]);

  if (!authLoading && !isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LightbulbOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>My Feature Requests</Typography>
          <Typography color="text.secondary" paragraph>
            Sign in to view the feature requests you've submitted.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>Sign In</Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LightbulbOutlinedIcon color="primary" />
          <Typography variant="h4">My Feature Requests</Typography>
        </Box>
        <Button variant="contained" component={RouterLink} to="/feature-request">
          Submit a new request
        </Button>
      </Box>

      {requests === null && !error && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {requests && requests.length === 0 && !error && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" paragraph>
            You haven't submitted any feature requests yet.
          </Typography>
          <Button variant="contained" component={RouterLink} to="/feature-request">
            Submit your first request
          </Button>
        </Paper>
      )}

      {requests && requests.length > 0 && (
        <Stack spacing={1.5}>
          {requests.map((r) => (
            <Accordion key={r.id} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[r.status] || r.status}
                    color={STATUS_COLORS[r.status] || 'default'}
                  />
                  <Typography sx={{ fontWeight: 600 }}>{r.category}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    {formatDate(r.created_at)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>As a</strong> {r.user_role === 'Other' && r.user_role_other ? r.user_role_other : r.user_role},
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>I need</strong> {r.need_description}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>So that</strong> {r.benefit_description}
                </Typography>
                {r.related_data_types && r.related_data_types.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', my: 1 }}>
                    {r.related_data_types.map((dt) => (
                      <Chip key={dt} size="small" variant="outlined" label={dt} />
                    ))}
                  </Box>
                )}
                {r.additional_context && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {r.additional_context}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Importance: {r.importance?.replace(/_/g, ' ')}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}
    </Container>
  );
}
